/**
 * Formatare de date și timp în limba aleasă de user (nu în `ro-RO` fix).
 * Tot ce afișează o dată în UI trece pe aici.
 */
import i18n from '@/i18n';

type DateInput = string | number | Date | null | undefined;

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function locale(): string {
  return i18n.resolvedLanguage || i18n.language || 'ro';
}

/** Dată + oră, ex. „12.10.2026, 14:05:09". `fallback` pentru valori lipsă. */
export function formatDateTime(value: DateInput, fallback = '—'): string {
  const date = toDate(value);
  return date ? date.toLocaleString(locale()) : fallback;
}

/** Doar data, ex. „12.10.2026". */
export function formatDate(value: DateInput, fallback = '—'): string {
  const date = toDate(value);
  return date ? date.toLocaleDateString(locale()) : fallback;
}

/** Doar ora, ex. „14:05:09". */
export function formatTime(value: DateInput, fallback = '—'): string {
  const date = toDate(value);
  return date ? date.toLocaleTimeString(locale()) : fallback;
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 86_400],
  ['month', 30 * 86_400],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
];

/**
 * Timp relativ în limba curentă, ex. „acum 3 ore" / „peste 2 zile" /
 * „3 hours ago". Sub un minut: „acum" / „now".
 */
export function formatRelative(value: DateInput, now: number = Date.now(), fallback = '—'): string {
  const date = toDate(value);
  if (!date) return fallback;
  const diffSec = Math.round((date.getTime() - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale(), { numeric: 'auto' });
  for (const [unit, seconds] of UNITS) {
    if (Math.abs(diffSec) >= seconds) return rtf.format(Math.round(diffSec / seconds), unit);
  }
  return rtf.format(0, 'second');
}
